import React, { useMemo } from "react";
import { Checkbox, Divider, FormControlLabel, Grid, Stack } from "@mui/material";
import ConfigHeader from "../ConfigHeader/config-header";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { FormExists, GetFormIsValid, GetSectionIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import {
  SettingRootField,
  UpdateItemSettingsRootOnBasket,
  UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import { GetOrderProductReadableType } from "../../../Utils/helpers";
import ConfigCommonSectionFormDelivery from "../ConfigCommon/config-common-section-form-delivery";
import FormSection from "../../Forms/FormSection/form-section";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import ConfigFormIcon from "../ConfigFormIcon";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { OrderProfileSettingsBase } from "../../../Core/Api/Types/order-profile-settings-base";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { TranskodeOptionSubtitleFileFormat } from "../../../Core/Api/Types/transkode-option-subtitle-file-format";
import { TranskodeOptionFrameRate } from "../../../Core/Api/Types/transkode-option-frame-rate";
import { Language } from "../../../Core/Api/Types/language";
import { SubtitleType } from "../../../Core/Api/Types/subtitle-type";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { DbUserInfos } from "../../../Core/Api/Types/db-user-infos";
import FormInputDate from "../../Forms/FormInputDate/form-input-date";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import ConfigManualTaskForm from "../ConfigManualTask/ConfigManualTaskForm/config-manual-task-form";
import { DbOrderManualTaskTemplateInfos } from "../../../Core/Api/Types/db-order-manual-task-template-infos";
import { TranskodeSubtitleFormatEnum } from "../../../Core/Api/Types/transkode-subtitle-format-enum";
import FormInputNumber from "../../Forms/FormInputNumber/form-input-number";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import dayjs from "dayjs";
import ConfigCommonSectionFormScheduling from "../ConfigCommon/config-common-section-form-scheduling";
import { useTranslation } from "react-i18next";
interface ConfigSubtitleProjectProps extends ConfigBaseProps {
  storages: DbStorageInfos[];
  maillists: MailingListDto[];
  templates: DbOrderManualTaskTemplateInfos[];
}

enum SubtitleGeneralType {
  SDH_CSA = "SDH_CSA",
  SDH_US = "SDH_US",
  SME = "SME",
  VM = "VM",
  VOST = "VOST",
}

enum SubtitleStandardType {
  Netflix = "Netflix",
  Itunes = "Itunes",
  Orange = "Orange",
  M6 = "M6",
  TF1 = "TF1",
  CanalPlus = "CanalPlus",
  Rue13 = "Rue13",
  MyTF1_VOD = "MyTF1_VOD",
}

enum SubtitleOpenCloseMode {
  Open = "Open",
  Closed = "Closed",
}

interface ScribeSubtitleProjectProfile extends OrderProfileSettingsBase {
  parentObjectId: number | null;
  fileFormat: TranskodeOptionSubtitleFileFormat;
  fileFormatSeparatedPartial: TranskodeOptionSubtitleFileFormat;
  frameRate: TranskodeOptionFrameRate;
  originLanguage: Language;
  outputLanguage: Language;
  type: SubtitleType;
  broadcasterId: string | null;
  generalType: SubtitleGeneralType;
  standardType: SubtitleStandardType;
  mode: SubtitleOpenCloseMode;
  customerId: number;
  timecodeInCue: string;
  episodeNb: number | null;
  contentType: string;
  separatedPartialFile: boolean;
  dueDate: Date | null;
  durationMin: number | null;
  techUserId: number;
}

const ConfigSubtitleProject: React.FC<ConfigSubtitleProjectProps> = (props) => {
  const { t } = useTranslation();
  const { handleSubmit, handleCancel, handleChangeProduct, item, storages, maillists, templates, ...otherProps } =
    props;
  const formId = `form-subproject-${item.id}`;
  const formValid = useAppSelector(GetFormIsValid(formId));
  const formExists = useAppSelector(FormExists(formId));
  const dispatch = useAppDispatch();

  // Item is updated automatically as it's passed as prop from parent and from redux orignally
  const settings: ScribeSubtitleProjectProfile = item.selectedProduct?.settings;

  // Update settings
  const handleUpdateParam = (field: SettingRootField, value: any) => {
    dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: field, value: value }));
  };

  const formValidTrigger = useMemo(() => {
    // In case of invalid, update item status
    if (!formValid && formExists && item.status !== BasketItemStatus.OptionsDone)
      dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

    return formValid;
  }, [formValid]);

  // Form validator
  const formSectionGeneral = useAppSelector(GetSectionIsValid(formId, "sectionGeneral"));
  const formSectionEditorial = useAppSelector(GetSectionIsValid(formId, "sectionEditorial"));
  const formSectionTech = useAppSelector(GetSectionIsValid(formId, "sectionTech"));

  return (
    <Stack width={"100%"} {...otherProps} display={formExists ? "" : "none"}>
      <ConfigHeader
        configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.SubtitleScribeProject}
        title={GetOrderProductReadableType(
          item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.SubtitleScribeProject
        )}
        handleCancel={handleCancel}
        handleChangeProduct={handleChangeProduct}
        itemId={item.id}
      />
      <Stack spacing={1.5} width={"100%"} {...otherProps}>
        <FormSection
          icon={<ConfigFormIcon icon="meta" />}
          title={t("common:generalInformationsLowerCase")}
          status={formSectionGeneral ? "complete" : "warning"}
          statusText={formSectionGeneral ? t("common:configured") : t("common:configurationRequired")}
        >
          <Grid container spacing={2}>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("common:customer")}
                fullWidth={true}
                options={item.selectedProduct?.settings.customers as DbCustomerInfos[]}
                value={
                  settings.customerId === 0
                    ? null
                    : item.selectedProduct?.settings.customers.find((x) => x.id === settings.customerId)
                }
                placeholder={t("field:label.selectCustomer")}
                onChange={(v: DbCustomerInfos) => {
                  handleUpdateParam(SettingRootField.SubProjectCustomerId, v.id);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectCustomerId, null);
                }}
                getOptionLabel={(value: any) => value.name + " - #" + value.customerCodeId}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter(
                    (a) =>
                      a.name?.toLowerCase().includes(s.inputValue.toLowerCase()) ||
                      a.customerCodeId?.toLowerCase().includes(s.inputValue.toLowerCase())
                  )
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionGeneral",
                  fieldId: "customer",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={6}>
              <FormAutocomplete
                label={t("field:label.assignedTechUser")}
                fullWidth={true}
                options={item.selectedProduct?.settings.users as DbUserInfos[]}
                value={
                  !settings.techUserId
                    ? null
                    : item.selectedProduct?.settings.users.find((x) => x.id === settings.techUserId)
                }
                placeholder={t("field:placeholder.selectUser")}
                onChange={(v: DbUserInfos) => {
                  handleUpdateParam(SettingRootField.SubProjectTechUserId, v.id);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectTechUserId, null);
                }}
                getOptionLabel={(value: any) => value.name + " - " + value.login}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) => x.filter((a) => a.name?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionGeneral",
                  fieldId: "techUser",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={6}>
              <FormInputText
                label={t("field:label.customId")}
                fullWidth={true}
                value={settings.broadcasterId}
                placeholder={t("field:placeholder.broadcasterCustomId")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectBroadcasterId, v.target.value);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectBroadcasterId, "");
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionGeneral",
                  fieldId: "customId",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={6}>
              <FormInputDate
                label={t("common:dueDate")}
                value={dayjs(settings.dueDate)}
                onChange={(v: Date) => {
                  handleUpdateParam(SettingRootField.SubProjectDueDate, v.toISOString());
                }}
                onReset={() => handleUpdateParam(SettingRootField.SubProjectDueDate, null)}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionGeneral",
                  fieldId: "dueDate",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection
          icon={<ConfigFormIcon icon="language" />}
          title={t("common:editorial")}
          status={formSectionEditorial ? "complete" : "warning"}
          statusText={formSectionEditorial ? t("common:configured") : t("common:configurationRequired")}
        >
          <Grid container spacing={2}>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.inputLanguage")}
                fullWidth={true}
                options={(item.selectedProduct?.settings.languages as Language[]) ?? []}
                value={settings.originLanguage ?? null}
                placeholder={t("field:placeholder.selectLanguage")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectInLang, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectInLang, null);
                }}
                getOptionLabel={(value: any) => value.isoCodeLocalized}
                isOptionEqualToValue={(option, value) => option.isoCodeLocalized === value.isoCodeLocalized}
                filterOptions={(x, s) =>
                  x.filter((a) => a.isoCodeLocalized?.toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionEditorial",
                  fieldId: "input-lang",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.outputLanguage")}
                fullWidth={true}
                options={(item.selectedProduct?.settings.languages as Language[]) ?? []}
                value={settings.outputLanguage ?? null}
                placeholder={t("field:placeholder.selectLanguage")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectOutLang, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectOutLang, null);
                }}
                getOptionLabel={(value: any) => value.isoCodeLocalized}
                isOptionEqualToValue={(option, value) => option.isoCodeLocalized === value.isoCodeLocalized}
                filterOptions={(x, s) =>
                  x.filter((a) => a.isoCodeLocalized?.toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionEditorial",
                  fieldId: "output-lang",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.subtitleType")}
                fullWidth={true}
                options={Object.keys(SubtitleGeneralType)}
                value={settings.generalType}
                placeholder={t("field:placeholder.selectType")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectGeneralType, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectGeneralType, null);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionEditorial",
                  fieldId: "general-type",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.subtitleStandard")}
                fullWidth={true}
                options={Object.keys(SubtitleGeneralType)}
                value={settings.standardType}
                placeholder={t("field:placeholder.selectStandard")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectStandardType, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectStandardType, null);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionEditorial",
                  fieldId: "std",
                  required: false,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.subtitleMode")}
                fullWidth={true}
                options={Object.keys(SubtitleOpenCloseMode)}
                value={settings.mode}
                placeholder={t("field:placeholder.selectMode")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectSubMode, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectSubMode, null);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionEditorial",
                  fieldId: "mode",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.destinationContent")}
                fullWidth={true}
                options={
                  (item.selectedProduct?.settings.contentTypeValues as DbMetadataFieldInfos).values?.map(
                    (content) => content.value
                  ) ?? []
                }
                value={settings.contentType}
                placeholder={t("field:placeholder.selectContent")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectDestContent, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectDestContent, null);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter((a) => a?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionEditorial",
                  fieldId: "content",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection
          icon={<ConfigFormIcon icon="settings" />}
          title={t("form:section.title.technicalSpecs")}
          status={formSectionTech ? "complete" : "warning"}
          statusText={formSectionTech ? t("common:configured") : t("common:configurationRequired")}
        >
          <Grid container spacing={2}>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.fileFormat")}
                fullWidth={true}
                options={
                  settings.fileFormat.values?.filter((x) => x.format !== TranskodeSubtitleFormatEnum._ASINPUT) ?? []
                }
                value={settings.fileFormat.selectedValue ?? null}
                placeholder={t("field:placeholder.selectFileFormat")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.FileFormat, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.FileFormat, null);
                }}
                getOptionLabel={(value: any) => value.label}
                isOptionEqualToValue={(option, value) => option.format === value.format}
                filterOptions={(x, s) => x.filter((a) => a.label?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTech",
                  fieldId: "format",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.fileFrameRate")}
                fullWidth={true}
                options={settings.frameRate.values ?? []}
                value={settings.frameRate.selectedValue ?? null}
                placeholder={t("field:placeholder.selectFrameRate")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.Fps, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.Fps, null);
                }}
                getOptionLabel={(value: any) => value.value.toString()}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.value?.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTech",
                  fieldId: "fps",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormAutocomplete
                label={t("field:label.subtitleContent")}
                fullWidth={true}
                options={Object.keys(SubtitleType)}
                value={settings.type}
                placeholder={t("field:placeholder.selectContent")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectType, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectType, null);
                }}
                getOptionLabel={(value: any) => value}
                isOptionEqualToValue={(option, value) => option === value}
                filterOptions={(x, s) =>
                  x.filter((a) => a.toString().toLowerCase().includes(s.inputValue.toLowerCase()))
                }
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTech",
                  fieldId: "content",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormInputText
                label={t("field:label.startTimecode")}
                fullWidth={true}
                value={settings.timecodeInCue}
                placeholder="00:00:00:00"
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectStartTimecode, v.target.value);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectStartTimecode, "");
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTech",
                  fieldId: "starttc",
                  required: true,
                  initialState: false,
                }}
              />
            </Grid>
            <Grid item md={4}>
              <FormInputNumber
                label={t("field:label.programDuration")}
                startAdornment={<AccessTimeIcon />}
                value={settings.durationMin}
                onChange={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectDuration, e.target.value);
                }}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTech",
                  fieldId: "duration",
                  required: true,
                  minValue: 1,
                  maxValue: 300,
                  initialState: true,
                  notDecimal: true,
                  positive: true,
                }}
                stackProps={{}}
              />
            </Grid>
            <Grid item md={4}></Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={settings.separatedPartialFile}
                    onClick={() =>
                      handleUpdateParam(SettingRootField.SubProjectSeparatedPartialFile, !settings.separatedPartialFile)
                    }
                  />
                }
                label={t("field:label.separatedPartialFile")}
              />
            </Grid>
            <Grid item md={4} display={settings.separatedPartialFile ? "block" : "none"}>
              <FormAutocomplete
                label={t("field:label.fileFormat")}
                fullWidth={true}
                options={
                  settings.fileFormatSeparatedPartial.values?.filter(
                    (x) => x.format !== TranskodeSubtitleFormatEnum._ASINPUT
                  ) ?? []
                }
                value={settings.fileFormatSeparatedPartial.selectedValue ?? null}
                placeholder={t("field:placeholder.selectFileFormat")}
                onChange={(v) => {
                  handleUpdateParam(SettingRootField.SubProjectSeparatedPartialFileFormat, v);
                }}
                onReset={(e) => {
                  handleUpdateParam(SettingRootField.SubProjectSeparatedPartialFileFormat, null);
                }}
                getOptionLabel={(value: any) => value.label}
                isOptionEqualToValue={(option, value) => option.format === value.format}
                filterOptions={(x, s) => x.filter((a) => a.label?.toLowerCase().includes(s.inputValue.toLowerCase()))}
                validationProps={{
                  formId: formId,
                  formSectionId: "sectionTech",
                  fieldId: "formatPartial",
                  required: settings.separatedPartialFile,
                  initialState: false,
                }}
              />
            </Grid>
          </Grid>
          <Grid item md={4}></Grid>
        </FormSection>
        <ConfigManualTaskForm item={item} formid={formId} defaultExpended={false} hideAlert={true} />
        <ConfigCommonSectionFormDelivery item={item} formid={formId} storages={storages} maillists={maillists} />
        <ConfigCommonSectionFormScheduling item={item} formid={formId} />
      </Stack>
      <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!formValidTrigger} />
    </Stack>
  );
};

export default ConfigSubtitleProject;
